import { useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { FieldError } from "@/components/ui/field"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  useGetCalUsersQuery,
  useGetTeamMembersQuery,
  useGetTeamsQuery,
} from "@/app/booking-calendar/bookingCalendarApi"
import { hostOptions, siteOptions } from "../searchCriteria"

/**
 * The item for "no choice". Radix reserves the empty string for its placeholder, so an item cannot
 * carry it; choosing this item sets the field back to "", which shows the placeholder of the same words.
 */
const ANY = "__any"

export interface HostPickerProps {
  /** The site's Cal team slug, or "" for every site. */
  siteSlug: string
  /** The host's Cal user id, or "" for anyone. */
  hostCalUserId: string
  error?: string
  onChange: (next: { siteSlug: string; hostCalUserId: string }) => void
}

/**
 * Search by host (AB#844): a site, which narrows the host list to its members and the search to that
 * site, and the host — anyone in the Cal organization when no site is chosen. The lists are read
 * only once the picker is reached (focused or opened), so a search that never uses it never asks for
 * them; both are kept for the session, as the calendar keeps them.
 */
export function HostPicker({ siteSlug, hostCalUserId, error, onChange }: HostPickerProps) {
  const [wanted, setWanted] = useState(false)
  const want = () => { if (!wanted) setWanted(true) }
  // Already chosen (a form restored with a host) means the names are needed to show it.
  const load = wanted || !!siteSlug || !!hostCalUserId

  const { data: teams = [] } = useGetTeamsQuery(load ? undefined : skipToken)
  const sites = siteOptions(teams)
  const site = teams.find(team => !!team.slug && team.slug === siteSlug)
  const { data: calUsers = [], isFetching: usersLoading } = useGetCalUsersQuery(load ? undefined : skipToken)
  const { data: members, isFetching: membersLoading } = useGetTeamMembersQuery(site ? { teamId: site.id } : skipToken)
  const hosts = hostOptions(calUsers, site ? members ?? [] : undefined)
  const loading = usersLoading || (!!site && membersLoading)

  return (
    <div className="flex flex-wrap items-start gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="search-site" className="text-xs">Site</Label>
        <Select
          value={siteSlug}
          // A different site has different members, so the host is chosen again from its list.
          onValueChange={value => onChange({ siteSlug: value === ANY ? "" : value, hostCalUserId: "" })}
          onOpenChange={open => { if (open) want() }}
        >
          <SelectTrigger id="search-site" className="w-48" onFocus={want} onPointerEnter={want}>
            <SelectValue placeholder="Every site" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Every site</SelectItem>
            {sites.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* As wide as its select, so a long site name in the hint wraps beneath it rather than widening
          the column and pushing Status and the dates sideways (AB#817). */}
      <div className="flex w-56 flex-col gap-1.5" data-testid="search-host-column">
        <Label htmlFor="search-host" className="text-xs">Host</Label>
        <Select
          value={hostCalUserId}
          onValueChange={value => onChange({ siteSlug, hostCalUserId: value === ANY ? "" : value })}
          onOpenChange={open => { if (open) want() }}
        >
          <SelectTrigger
            id="search-host"
            className="w-56"
            onFocus={want}
            onPointerEnter={want}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "search-host-error" : "search-host-hint"}
          >
            <SelectValue placeholder="Anyone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Anyone</SelectItem>
            {hosts.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
            {loading && hosts.length === 0 && <SelectItem value="__loading" disabled>Loading…</SelectItem>}
          </SelectContent>
        </Select>
        {error
          ? <FieldError id="search-host-error" className="break-words">{error}</FieldError>
          : <p id="search-host-hint" className="break-words text-xs text-muted-foreground">{site ? `Hosts at ${site.name}; only their appointments there.` : "Their appointments at every site."}</p>}
      </div>
    </div>
  )
}
